import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

function pickRfqFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.rfq && typeof dataBlock.rfq === 'object') {
    return dataBlock.rfq
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.rfqs == null &&
    (dataBlock._id != null || dataBlock.rfqNumber != null || dataBlock.rfqNo != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.rfqs
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((r) => String(r._id || r.id) === String(id))
  return found || list[0]
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDateTime(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function formatAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function isDecimalInput(value) {
  return value === '' || /^\d*\.?\d*$/.test(String(value))
}

function parseDecimalValue(value) {
  if (value == null || value === '') return NaN
  const num = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(num) ? num : NaN
}

function getMaterialBoqLines(material) {
  if (Array.isArray(material?.boqItems) && material.boqItems.length > 0) {
    return material.boqItems
  }
  if (Array.isArray(material?.boq) && material.boq.length > 0) {
    return material.boq
  }
  return []
}

function getBoqLineId(item) {
  return String(item?.boq_id || item?._id || item?.id || '').trim()
}

/** Flatten RFQ materials into BOQ target-price rows for the Price Matching form. */
function collectRfqBoqTargetRows(rfq) {
  const materials = Array.isArray(rfq?.materials) ? rfq.materials : []
  const rows = []
  materials.forEach((material, matIndex) => {
    const lineNo = material.lineNo != null ? material.lineNo : matIndex + 1
    const boqLines = getMaterialBoqLines(material)
    boqLines.forEach((boqItem, boqIndex) => {
      const boqId = getBoqLineId(boqItem)
      const boqNo = String(boqItem.boqNo || `${lineNo}.${boqIndex + 1}`).trim()
      const key = boqId || `no:${boqNo}`
      rows.push({
        key,
        boq_id: boqId,
        boqNo,
        materialDescription: boqItem.materialDescription || '-',
        parentDescription: material.materialDescription || '',
        quantity: boqItem.quantity,
        uom: boqItem.uom || '',
        currentTarget:
          boqItem.targetPrice != null && boqItem.targetPrice !== ''
            ? String(boqItem.targetPrice)
            : ''
      })
    })
  })
  return rows
}

function getSupplierBoqTargetValueMap(supplier) {
  const map = {}
  const quotes = Array.isArray(supplier?.materialQuotes)
    ? supplier.materialQuotes
    : []
  quotes.forEach((quote) => {
    const boqLines = getMaterialBoqLines(quote)
    boqLines.forEach((item) => {
      const effective = getLineEffectiveTargetPrice(item)
      if (!Number.isFinite(effective) || effective <= 0) {
        if (item?.targetPrice == null || item.targetPrice === '') return
      }
      const value = Number.isFinite(effective)
        ? String(effective)
        : String(item.targetPrice)
      const id = getBoqLineId(item)
      const no = String(item.boqNo || '').trim()
      if (id) map[`id:${id}`] = value
      if (no) map[`no:${no}`] = value
    })
  })
  return map
}

function buildBoqTargetInputsFromRfq(rfq, supplier = null) {
  const rows = collectRfqBoqTargetRows(rfq)
  const supplierMap = supplier ? getSupplierBoqTargetValueMap(supplier) : {}
  const inputs = {}
  rows.forEach((row) => {
    const fromSupplier =
      (row.boq_id && supplierMap[`id:${row.boq_id}`]) ||
      (row.boqNo && supplierMap[`no:${row.boqNo}`]) ||
      ''
    inputs[row.key] = fromSupplier || row.currentTarget || ''
  })
  return inputs
}

function displayQuoteHsn(quote) {
  const value = String(quote?.hsn || quote?.hsnCode || '').trim()
  return value || '-'
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getSupplierAttachments(supplier) {
  if (!Array.isArray(supplier?.attachments)) return []
  return supplier.attachments.map(mapAttachmentFromApi).filter((att) => att.s3Key)
}

const AttachmentTileGrid = ({ attachments, downloadingS3Key, onDownload, disabled }) => {
  if (!attachments?.length) return null

  return (
    <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType
        )
        const isDownloading = downloadingS3Key === att.s3Key
        return (
          <button
            key={att.s3Key}
            type="button"
            className="border-0 text-center"
            title={`Open ${fileName}`}
            onClick={() => onDownload(att.s3Key, att.url)}
            disabled={disabled || isDownloading}
            style={{
              width: '92px',
              padding: '10px 8px',
              borderRadius: '10px',
              backgroundColor: fileMeta.bg,
              border: `1px solid ${fileMeta.color}33`,
              cursor: disabled || isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: fileMeta.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                marginBottom: '8px'
              }}
            >
              {isDownloading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className={`fa-solid ${fileMeta.icon}`} />
              )}
            </div>
            <div
              title={fileName}
              style={{
                fontSize: '11px',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                color: '#212529',
                width: '100%'
              }}
            >
              {fileName.length > 22 ? `${fileName.slice(0, 19)}...` : fileName}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

function getSupplierEmailLink(rfqId, supplier) {
  if (supplier?.quoteLink) return supplier.quoteLink
  if (supplier?.emailLink) return supplier.emailLink
  const mail = String(supplier?.supplierMailId || '').trim()
  if (!rfqId || !mail) return ''
  const supplierId = String(
    supplier?.supplierId || supplier?._id || supplier?.id || ''
  ).trim()
  const base = `${window.location.origin}/rfqSuppliers/${rfqId}/${encodeURIComponent(mail)}`
  return supplierId ? `${base}/${supplierId}` : base
}

function applyResendEmailToRfq(prevRfq, responseData, supplierEmail) {
  if (!prevRfq) return prevRfq

  const data = responseData?.data
  const quoteLink = data?.result?.quoteLink || ''
  const mailKey = supplierEmail.trim().toLowerCase()

  const nextSuppliers = Array.isArray(prevRfq.suppliers)
    ? prevRfq.suppliers.map((supplier) => {
        if (String(supplier.supplierMailId || '').trim().toLowerCase() !== mailKey) {
          return supplier
        }
        return {
          ...supplier,
          ...(data?.supplierName ? { supplierName: data.supplierName } : {}),
          ...(quoteLink ? { quoteLink, emailLink: quoteLink } : {})
        }
      })
    : prevRfq.suppliers

  return {
    ...prevRfq,
    isMailSent: true,
    ...(data?.pdfLink ? { pdfLink: data.pdfLink } : {}),
    ...(Array.isArray(nextSuppliers) ? { suppliers: nextSuppliers } : {})
  }
}

function supplierHasSubmittedQuote(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.hasQuoteSubmitted === true) return true
  if (supplier.quoteSubmittedAt) return true
  if (Array.isArray(supplier.materialQuotes) && supplier.materialQuotes.length > 0) {
    return true
  }
  if (Array.isArray(supplier.terms) && supplier.terms.length > 0) return true
  return false
}

function supplierHasQuotedPrice(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.quoteSubmittedAt) return true
  if (supplier.hasQuoteSubmitted === true) return true
  const quotes = Array.isArray(supplier.materialQuotes)
    ? supplier.materialQuotes
    : []
  return quotes.some((quote) => {
    const quoted = Number(quote?.quotedPrice)
    const basic = Number(quote?.basicPrice)
    const total = Number(quote?.totalValue)
    if (Number.isFinite(quoted) && quoted > 0) return true
    if (Number.isFinite(basic) && basic > 0) return true
    if (Number.isFinite(total) && total > 0) return true
    const boq = Array.isArray(quote?.boq) ? quote.boq : []
    return boq.some((item) => {
      const qp = Number(item?.quotedPrice)
      const bp = Number(item?.basicPrice)
      return (
        (Number.isFinite(qp) && qp > 0) || (Number.isFinite(bp) && bp > 0)
      )
    })
  })
}

/** True when at least one supplier has quoted a price (Price Matching can start). */
function rfqHasAnySupplierQuote(rfq, suppliersList = []) {
  if (Array.isArray(suppliersList) && suppliersList.some(supplierHasQuotedPrice)) {
    return true
  }
  const history = getTargetPriceQuoteHistory(rfq)
  if (
    history.beforeTargetPrice.length > 0 ||
    history.afterTargetPrice.length > 0
  ) {
    return true
  }
  return false
}

function supplierHasOpenedLink(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (
    supplier.hasLinkOpened === true ||
    supplier.isLinkOpened === true ||
    supplier.linkOpened === true ||
    supplier.hasOpenedLink === true
  ) {
    return true
  }
  if (supplier.linkOpenedAt || supplier.linkVisitedAt || supplier.openedAt) return true
  return false
}

function getSupplierViewButtonProps(supplier) {
  if (supplierHasSubmittedQuote(supplier)) {
    return {
      className: 'btn btn-sm btn-blue',
      title: 'Quote submitted — view details'
    }
  }
  if (supplierHasOpenedLink(supplier)) {
    return {
      className: 'btn btn-sm btn-warning',
      title: 'Link opened — quote not submitted yet'
    }
  }
  return {
    className: 'btn btn-sm btn-secondary',
    title: 'Link not opened yet'
  }
}

function hasSupplierQuotes(supplier) {
  return supplierHasSubmittedQuote(supplier)
}

function getSupplierTerms(supplier) {
  return Array.isArray(supplier?.terms) ? supplier.terms : []
}

function getQuoteGrossTotal(quoteLike) {
  if (!quoteLike || typeof quoteLike !== 'object') return NaN
  const direct = Number(
    quoteLike.grossTotalValue ?? quoteLike.quote?.grossTotalValue
  )
  if (Number.isFinite(direct)) return direct
  return NaN
}

/** Normalize get-rfq-by-id target price quote history payload. */
function getTargetPriceQuoteHistory(rfq) {
  if (!rfq || typeof rfq !== 'object') {
    return {
      targetPrice: null,
      beforeTargetPrice: [],
      afterTargetPrice: [],
      suppliersWithBeforeCount: 0,
      suppliersWithAfterCount: 0
    }
  }

  const bundle =
    rfq.targetPriceQuotes && typeof rfq.targetPriceQuotes === 'object'
      ? rfq.targetPriceQuotes
      : rfq

  const before = Array.isArray(bundle.beforeTargetPrice)
    ? bundle.beforeTargetPrice
    : Array.isArray(rfq.beforeTargetPrice)
      ? rfq.beforeTargetPrice
      : []
  const after = Array.isArray(bundle.afterTargetPrice)
    ? bundle.afterTargetPrice
    : Array.isArray(rfq.afterTargetPrice)
      ? rfq.afterTargetPrice
      : []

  const targetPrice =
    bundle.targetPrice != null && bundle.targetPrice !== ''
      ? bundle.targetPrice
      : rfq.targetPrice

  return {
    targetPrice,
    beforeTargetPrice: before,
    afterTargetPrice: after,
    suppliersWithBeforeCount:
      Number(bundle.suppliersWithBeforeCount) || before.length,
    suppliersWithAfterCount:
      Number(bundle.suppliersWithAfterCount) || after.length
  }
}

function getLineQuotedPrice(line) {
  if (!line || typeof line !== 'object') return NaN
  const quoted = Number(line.quotedPrice)
  if (Number.isFinite(quoted)) return quoted
  const basic = Number(line.basicPrice)
  return Number.isFinite(basic) ? basic : NaN
}

function getLineEffectiveTargetPrice(line) {
  if (!line || typeof line !== 'object') return NaN
  const candidates = [
    line.targetPrice,
    line.supplierTargetPrice,
    line.allSuppliersTargetPrice
  ]
  for (const value of candidates) {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
  }
  return NaN
}

function getLinePriceDifference(line) {
  if (!line || typeof line !== 'object') return NaN
  const direct = Number(line.difference)
  if (Number.isFinite(direct)) return direct
  const quoted = getLineQuotedPrice(line)
  const target = getLineEffectiveTargetPrice(line)
  if (!Number.isFinite(quoted) || !Number.isFinite(target)) return NaN
  return quoted - target
}

function lineNeedsPriceMatching(line) {
  if (!line || typeof line !== 'object') return false
  if (line.needsPriceMatching === true) return true
  if (line.needsPriceMatching === false) return false
  const diff = getLinePriceDifference(line)
  return Number.isFinite(diff) && diff > 0
}

function supplierNeedsPriceMatching(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.needsPriceMatching === true) return true
  if (supplier.needsPriceMatching === false) return false
  const quotes = Array.isArray(supplier.materialQuotes)
    ? supplier.materialQuotes
    : []
  return quotes.some((quote) => {
    if (lineNeedsPriceMatching(quote)) return true
    const boqLines = Array.isArray(quote?.boq) ? quote.boq : []
    return boqLines.some(lineNeedsPriceMatching)
  })
}

function getBoqLineSupplierEntries(line) {
  if (!line || typeof line !== 'object') return []
  if (Array.isArray(line.suppliers)) return line.suppliers
  if (Array.isArray(line.quotes)) return line.quotes
  if (Array.isArray(line.supplierQuotes)) return line.supplierQuotes
  return []
}

function findBoqQuotedInHistoryEntry(entry, boqNo, boqId) {
  if (!entry) return NaN
  const finalRev = getFinalRevisionFromEntry(entry)
  const quote = pickHistoryQuoteObject(entry, finalRev)
  const quotes = Array.isArray(quote.materialQuotes) ? quote.materialQuotes : []
  for (const mq of quotes) {
    const boqLines = Array.isArray(mq?.boq) ? mq.boq : []
    for (const item of boqLines) {
      const itemId = getBoqLineId(item)
      const itemNo = String(item.boqNo || '').trim()
      if (
        (boqId && itemId && String(itemId) === String(boqId)) ||
        (boqNo && itemNo && itemNo === String(boqNo).trim())
      ) {
        return getLineQuotedPrice(item)
      }
    }
  }
  return NaN
}

function getBeforeQuotedForSupplierBoq(rfq, supplierIdentity, boqNo, boqId) {
  const history = getTargetPriceQuoteHistory(rfq)
  const beforeEntry =
    (Array.isArray(history.beforeTargetPrice) ? history.beforeTargetPrice : []).find(
      (entry) => suppliersMatchIdentity(entry, supplierIdentity)
    ) || null
  if (!beforeEntry) return NaN
  return findBoqQuotedInHistoryEntry(beforeEntry, boqNo, boqId)
}

function supplierHasAfterMatchingQuote(rfq, supplierIdentity) {
  if (!rfq || !supplierIdentity) return false
  const history = getTargetPriceQuoteHistory(rfq)
  return (Array.isArray(history.afterTargetPrice) ? history.afterTargetPrice : []).some(
    (entry) => suppliersMatchIdentity(entry, supplierIdentity)
  )
}

/**
 * Before = snapshot when price matching started.
 * If that snapshot is missing and the supplier has not revised after matching,
 * their current Quoted is still the before-matching quote (fallback).
 * After they revise via Notify → supplier portal, Quoted updates; Before stays
 * from history (or stays equal until a before snapshot exists).
 */
function getEntryBeforeQuotedPrice(entry, line, rfq) {
  if (!entry) return NaN
  const direct = Number(
    entry.beforeQuotedPrice ??
      entry.beforePrice ??
      entry.quotedPriceBefore ??
      entry.beforeTargetQuotedPrice
  )
  if (Number.isFinite(direct)) return direct

  const fromHistory = getBeforeQuotedForSupplierBoq(
    rfq,
    entry,
    line?.boqNo,
    line?.boq_id || line?.boqId || getBoqLineId(line)
  )
  if (Number.isFinite(fromHistory)) return fromHistory

  // No before history yet — current quote is still the pre-revision value.
  if (!supplierHasAfterMatchingQuote(rfq, entry)) {
    const currentQuoted = getLineQuotedPrice(entry)
    if (Number.isFinite(currentQuoted)) return currentQuoted
  }
  return NaN
}

/** Normalize get-rfq-by-id boqPriceMatching.boqLines payload. */
function getBoqPriceMatchingBundle(rfq) {
  if (!rfq || typeof rfq !== 'object') {
    return { boqLines: [] }
  }
  const bundle =
    rfq.boqPriceMatching && typeof rfq.boqPriceMatching === 'object'
      ? rfq.boqPriceMatching
      : {}
  const lines = Array.isArray(bundle.boqLines)
    ? bundle.boqLines
    : Array.isArray(rfq.boqPriceMatchingLines)
      ? rfq.boqPriceMatchingLines
      : []
  return { ...bundle, boqLines: lines }
}

/**
 * Build per-BOQ supplier quoted-vs-target rows from live supplier materialQuotes
 * when top-level boqPriceMatching.boqLines is absent.
 */
function buildBoqPriceMatchingLinesFromSuppliers(rfq, suppliers = []) {
  const materials = Array.isArray(rfq?.materials) ? rfq.materials : []
  const quotedSuppliers = (Array.isArray(suppliers) ? suppliers : []).filter(
    supplierHasQuotedPrice
  )
  if (materials.length === 0 || quotedSuppliers.length === 0) return []

  const lines = []
  materials.forEach((material, matIndex) => {
    const lineNo = material.lineNo != null ? material.lineNo : matIndex + 1
    const boqLines = getMaterialBoqLines(material)
    boqLines.forEach((boqItem, boqIndex) => {
      const boqId = getBoqLineId(boqItem)
      const boqNo = String(boqItem.boqNo || `${lineNo}.${boqIndex + 1}`).trim()
      const rfqTarget = getLineEffectiveTargetPrice(boqItem)
      const supplierEntries = []

      quotedSuppliers.forEach((supplier) => {
        const quotes = Array.isArray(supplier.materialQuotes)
          ? supplier.materialQuotes
          : []
        let matched = null
        quotes.forEach((quote) => {
          const quoteBoq = Array.isArray(quote?.boq) ? quote.boq : []
          quoteBoq.forEach((item) => {
            if (matched) return
            const itemId = getBoqLineId(item)
            const itemNo = String(item.boqNo || '').trim()
            if (
              (boqId && itemId && itemId === boqId) ||
              (boqNo && itemNo && itemNo === boqNo)
            ) {
              matched = item
            }
          })
        })
        if (!matched) return
        const quotedPrice = getLineQuotedPrice(matched)
        const targetPrice = getLineEffectiveTargetPrice(matched)
        const effectiveTarget = Number.isFinite(targetPrice)
          ? targetPrice
          : rfqTarget
        const difference = Number.isFinite(Number(matched.difference))
          ? Number(matched.difference)
          : Number.isFinite(quotedPrice) && Number.isFinite(effectiveTarget)
            ? quotedPrice - effectiveTarget
            : NaN
        const beforeQuotedPrice = getBeforeQuotedForSupplierBoq(
          rfq,
          supplier,
          boqNo,
          boqId
        )
        supplierEntries.push({
          supplierId: supplier.supplierId,
          supplierName: supplier.supplierName,
          supplierMailId: supplier.supplierMailId,
          quotedPrice,
          beforeQuotedPrice: Number.isFinite(beforeQuotedPrice)
            ? beforeQuotedPrice
            : undefined,
          basicPrice: matched.basicPrice,
          targetPrice: effectiveTarget,
          allSuppliersTargetPrice: matched.allSuppliersTargetPrice,
          supplierTargetPrice: matched.supplierTargetPrice,
          difference,
          needsPriceMatching:
            matched.needsPriceMatching === true ||
            matched.needsPriceMatching === false
              ? matched.needsPriceMatching
              : Number.isFinite(difference) && difference > 0
        })
      })

      if (supplierEntries.length === 0) return
      lines.push({
        boq_id: boqId,
        boqNo,
        materialDescription: boqItem.materialDescription || '-',
        parentDescription: material.materialDescription || '',
        quantity: boqItem.quantity,
        uom: boqItem.uom || '',
        targetPrice: Number.isFinite(rfqTarget)
          ? rfqTarget
          : supplierEntries.find((s) =>
              Number.isFinite(Number(s.targetPrice))
            )?.targetPrice,
        suppliers: supplierEntries,
        needsPriceMatching: supplierEntries.some(lineNeedsPriceMatching)
      })
    })
  })
  return lines
}

function getResolvedBoqPriceMatchingLines(rfq, suppliers = []) {
  const fromApi = getBoqPriceMatchingBundle(rfq).boqLines
  if (fromApi.length > 0) return fromApi
  const finalSuppliers = getSuppliersForFinalBoqComparison(rfq, suppliers)
  return buildBoqPriceMatchingLinesFromSuppliers(rfq, finalSuppliers)
}

function normalizePriceMatchingSupplierEntry(entry) {
  if (!entry) return null
  if (typeof entry === 'string') {
    const value = String(entry).trim()
    if (!value) return null
    return {
      supplierId: value,
      supplierMailId: value.includes('@') ? value : '',
      supplierName: value
    }
  }
  if (typeof entry !== 'object') return null
  return entry
}

function suppliersMatchIdentity(a, b) {
  if (!a || !b) return false
  if (a.supplierId && b.supplierId && String(a.supplierId) === String(b.supplierId)) {
    return true
  }
  const mailA = String(a.supplierMailId || '')
    .trim()
    .toLowerCase()
  const mailB = String(b.supplierMailId || '')
    .trim()
    .toLowerCase()
  return Boolean(mailA && mailB && mailA === mailB)
}

/** Suppliers who still need price matching (API list, else derived). */
function getPriceMatchingSuppliersList(rfq, allSuppliers = []) {
  const suppliers = Array.isArray(allSuppliers) ? allSuppliers : []
  const raw = Array.isArray(rfq?.priceMatchingSuppliers)
    ? rfq.priceMatchingSuppliers
    : []
  if (raw.length > 0) {
    return raw
      .map(normalizePriceMatchingSupplierEntry)
      .filter(Boolean)
      .map((entry) => {
        const match = suppliers.find((s) => suppliersMatchIdentity(s, entry))
        return match
          ? { ...match, ...entry, needsPriceMatching: true }
          : { ...entry, needsPriceMatching: true }
      })
  }
  return suppliers.filter(supplierNeedsPriceMatching)
}

function hasLivePriceMatchingData(rfq, suppliers = []) {
  if (Array.isArray(rfq?.priceMatchingSuppliers) && rfq.priceMatchingSuppliers.length > 0) {
    return true
  }
  if (getBoqPriceMatchingBundle(rfq).boqLines.length > 0) return true
  return (Array.isArray(suppliers) ? suppliers : []).some((supplier) => {
    const quotes = Array.isArray(supplier?.materialQuotes)
      ? supplier.materialQuotes
      : []
    return quotes.some((quote) => {
      if (getLineEffectiveTargetPrice(quote) > 0) return true
      const boqLines = Array.isArray(quote?.boq) ? quote.boq : []
      return boqLines.some((item) => getLineEffectiveTargetPrice(item) > 0)
    })
  })
}

/** True when RFQ total or any BOQ/material target price has been set (> 0). */
function rfqHasSavedPriceMatchingTargets(rfq, suppliers = []) {
  if (!rfq || typeof rfq !== 'object') return false
  if (
    rfq.targetPrice != null &&
    rfq.targetPrice !== '' &&
    Number.isFinite(Number(rfq.targetPrice)) &&
    Number(rfq.targetPrice) > 0
  ) {
    return true
  }

  const materials = Array.isArray(rfq.materials) ? rfq.materials : []
  for (const material of materials) {
    if (getLineEffectiveTargetPrice(material) > 0) return true
    if (
      getMaterialBoqLines(material).some(
        (item) => getLineEffectiveTargetPrice(item) > 0
      )
    ) {
      return true
    }
  }

  const apiLines = getBoqPriceMatchingBundle(rfq).boqLines
  for (const line of apiLines) {
    if (getLineEffectiveTargetPrice(line) > 0) return true
    if (
      getBoqLineSupplierEntries(line).some(
        (entry) => getLineEffectiveTargetPrice(entry) > 0
      )
    ) {
      return true
    }
  }

  return (Array.isArray(suppliers) ? suppliers : []).some((supplier) => {
    const quotes = Array.isArray(supplier?.materialQuotes)
      ? supplier.materialQuotes
      : []
    return quotes.some((quote) => {
      if (getLineEffectiveTargetPrice(quote) > 0) return true
      return (Array.isArray(quote?.boq) ? quote.boq : []).some(
        (item) => getLineEffectiveTargetPrice(item) > 0
      )
    })
  })
}

function pickHistoryQuoteObject(entry, quoteOverride = null) {
  const candidates = [
    quoteOverride,
    quoteOverride?.quote,
    entry?.quote,
    entry
  ].filter((item) => item && typeof item === 'object')

  for (const candidate of candidates) {
    if (Array.isArray(candidate.materialQuotes)) return candidate
  }
  return candidates[0] || {}
}

function getEntryRevisions(entry) {
  if (Array.isArray(entry?.allRevisions) && entry.allRevisions.length > 0) {
    return entry.allRevisions
  }
  if (entry?.quote && typeof entry.quote === 'object') return [entry.quote]
  return []
}

/** Latest / final revision for a before or after history entry. */
function getFinalRevisionFromEntry(entry) {
  const revisions = getEntryRevisions(entry)
  if (revisions.length === 0) {
    if (entry?.quote && typeof entry.quote === 'object') return entry.quote
    return entry && typeof entry === 'object' ? entry : null
  }
  return revisions.reduce((best, rev) => {
    const bestNo = Number(best?.revisionNo)
    const revNo = Number(rev?.revisionNo)
    if (Number.isFinite(revNo) && Number.isFinite(bestNo)) {
      return revNo >= bestNo ? rev : best
    }
    if (Number.isFinite(revNo) && !Number.isFinite(bestNo)) return rev
    return best
  }, revisions[revisions.length - 1])
}

/**
 * Prefer each supplier's final after-matching revision materialQuotes for BOQ
 * comparison; fall back to live supplier quotes (already the current final).
 */
function getSuppliersForFinalBoqComparison(rfq, suppliers = []) {
  const history = getTargetPriceQuoteHistory(rfq)
  const after = Array.isArray(history.afterTargetPrice)
    ? history.afterTargetPrice
    : []
  return (Array.isArray(suppliers) ? suppliers : []).map((supplier) => {
    const afterEntry =
      after.find((entry) => suppliersMatchIdentity(entry, supplier)) || null
    if (!afterEntry) return supplier
    const finalRev = getFinalRevisionFromEntry(afterEntry)
    const quote = pickHistoryQuoteObject(afterEntry, finalRev)
    if (!Array.isArray(quote.materialQuotes) || quote.materialQuotes.length === 0) {
      return supplier
    }
    return {
      ...supplier,
      materialQuotes: quote.materialQuotes,
      grossTotalBasicPrice:
        quote.grossTotalBasicPrice ?? supplier.grossTotalBasicPrice,
      grossGstValue: quote.grossGstValue ?? supplier.grossGstValue,
      grossTotalValue: quote.grossTotalValue ?? supplier.grossTotalValue,
      quoteSubmittedAt:
        quote.quoteSubmittedAt || supplier.quoteSubmittedAt,
      _revisionNo: finalRev?.revisionNo ?? quote?.revisionNo ?? null
    }
  })
}

function formatSignedAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  const abs = Math.abs(num).toFixed(2)
  if (num > 0) return `+${abs}`
  if (num < 0) return `-${abs}`
  return abs
}

function getRfqTermsRows(rfq) {
  if (Array.isArray(rfq?.terms) && rfq.terms.length > 0) {
    return rfq.terms.map((t) => ({
      rfqTerm: t.rfqTerm || '-',
      termsResponse: t.termsResponse || '-'
    }))
  }
  if (rfq?.rfqTerms) {
    return [{ rfqTerm: rfq.rfqTerms, termsResponse: rfq.termsResponse || '-' }]
  }
  return []
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

const RfqView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [rfq, setRfq] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showTargetPriceModal, setShowTargetPriceModal] = useState(false)
  const [targetPriceModalAnimating, setTargetPriceModalAnimating] = useState(false)
  const [targetPriceInput, setTargetPriceInput] = useState('')
  const [priceMatchApplyTo, setPriceMatchApplyTo] = useState('all') // all | supplier
  const [priceMatchSupplierMailId, setPriceMatchSupplierMailId] = useState('')
  const [boqTargetInputs, setBoqTargetInputs] = useState({})
  const [isSavingTargetPrice, setIsSavingTargetPrice] = useState(false)
  const [showTargetPriceSuppliers, setShowTargetPriceSuppliers] = useState(false)
  const [targetPriceModalTab, setTargetPriceModalTab] = useState('history') // history | notify | edit
  const [resendingFor, setResendingFor] = useState('')
  const [sendingPriceMatchFor, setSendingPriceMatchFor] = useState('')
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)
  const targetPriceModalCloseTimerRef = useRef(null)

  const rfqOwnerAttachments = useMemo(() => {
    if (!Array.isArray(rfq?.rfqOwner)) return []
    return rfq.rfqOwner.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }, [rfq?.rfqOwner])

  // Strict order: 1) get-rfq-by-id → 2) get-user (never parallel on same API).
  useEffect(() => {
    if (!id) return

    let cancelled = false

    const loadInOrder = async () => {
      setIsLoading(true)
      try {
        const rfqResponse = await axios.post(API_URL, {
          action: 'get-rfq-by-id',
          rfqid: id
        })
        if (cancelled) return

        const responseData = normalizeApiPayload(parseApiResponse(rfqResponse.data))

        if (isApiSuccess(responseData)) {
          const row = pickRfqFromResponse(responseData, id)
          if (row) {
            setRfq(row)
          } else {
            toast.error('RFQ not found', { autoClose: 2000 })
            navigate('/rfq')
            return
          }
        } else {
          toast.error(responseData?.message || 'Failed to fetch RFQ details', {
            autoClose: 2000
          })
          navigate('/rfq')
          return
        }
      } catch (error) {
        if (cancelled) return
        console.error('Fetch RFQ error:', error)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(
          errData?.message || 'An error occurred while fetching RFQ details',
          { autoClose: 2000 }
        )
        navigate('/rfq')
        return
      } finally {
        if (!cancelled) setIsLoading(false)
      }

      // 2nd call only after RFQ response is done
      const userId = localStorage.getItem('id')
      if (!userId || cancelled) return

      try {
        const userResponse = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        if (cancelled) return
        const responseData = parseApiResponse(userResponse.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Fetch user email error:', error)
        }
      }
    }

    loadInOrder()

    return () => {
      cancelled = true
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
    }
  }, [id, navigate])

  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-users',
        page: 1,
        limit: 25,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: term
      })
      if (requestId !== ccSearchRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const users = Array.isArray(responseData?.users) ? responseData.users : []
      setCcUserSuggestions(
        users.filter((user) => isValidEmailAddress(user?.email))
      )
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastSeparator = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastSeparator >= 0 ? `${value.slice(0, lastSeparator + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (existing.some((item) => item.toLowerCase() === selected.toLowerCase())) {
        return base
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

  const handleResendEmail = async (supplier) => {
    const supplierEmail = String(supplier?.supplierMailId || '').trim()
    if (!id) {
      toast.error('RFQ ID is missing', { autoClose: 2000 })
      return
    }
    if (!supplierEmail) {
      toast.error('Supplier email is missing', { autoClose: 2000 })
      return
    }
    if (String(rfq?.status).toLowerCase() !== 'active') {
      toast.error('RFQ must be Active before sending email', { autoClose: 2000 })
      return
    }
    if (!rfq?.pdfLink?.trim()) {
      toast.error('PDF is not available for this RFQ', { autoClose: 2000 })
      return
    }

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }
    if (ccEmails.length === 0) {
      toast.error('Could not get your email for CC. Add CC emails or check your profile.', {
        autoClose: 2000
      })
      return
    }

    setResendingFor(supplierEmail)
    try {
      const response = await axios.post(API_URL, {
        action: 'resend-rfq-email',
        id,
        supplierMailId: supplierEmail,
        userid: userId,
        ccEmails
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const result = responseData?.data?.result
        const failCount = Number(responseData?.data?.failCount) || 0
        const sendOk = result?.success !== false && failCount === 0

        if (!sendOk) {
          toast.error(
            result?.message || responseData?.message || 'Failed to resend email',
            { autoClose: 2000 }
          )
          return
        }

        toast.success(
          responseData?.message || `Email resent to ${supplierEmail}`,
          { autoClose: 2000 }
        )
        setRfq((prev) => applyResendEmailToRfq(prev, responseData, supplierEmail))
      } else {
        toast.error(responseData?.message || 'Failed to resend email', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Resend RFQ email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while resending email', {
        autoClose: 2000
      })
    } finally {
      setResendingFor('')
    }
  }

  const handleSendPriceMatchingEmail = async (supplier) => {
    const supplierEmail = String(supplier?.supplierMailId || '').trim()
    if (!id) {
      toast.error('RFQ ID is missing', { autoClose: 2000 })
      return
    }
    if (!supplierEmail) {
      toast.error('Supplier email is missing', { autoClose: 2000 })
      return
    }

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setSendingPriceMatchFor(supplierEmail)
    try {
      const response = await axios.post(API_URL, {
        action: 'send-rfq-price-matching-email',
        id,
        supplierMailId: supplierEmail,
        currentUserId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message ||
            `Price matching email sent to ${supplierEmail}`,
          { autoClose: 2000 }
        )
      } else {
        toast.error(
          responseData?.message || 'Failed to send price matching email',
          { autoClose: 2000 }
        )
      }
    } catch (error) {
      console.error('Send RFQ price matching email error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message ||
          'An error occurred while sending price matching email',
        { autoClose: 2000 }
      )
    } finally {
      setSendingPriceMatchFor('')
    }
  }

  const handleCopySupplierLink = async (supplier) => {
    const link = getSupplierEmailLink(id, supplier)
    if (!link) {
      toast.error('Supplier link is not available', { autoClose: 2000 })
      return
    }

    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copied to clipboard', { autoClose: 2000 })
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = link
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        toast.success('Link copied to clipboard', { autoClose: 2000 })
      } catch {
        toast.error('Failed to copy link', { autoClose: 2000 })
      }
      document.body.removeChild(textarea)
    }
  }

  const openQuoteModal = (supplier) => {
    setSelectedSupplier(supplier)
    setShowQuoteModal(true)
  }

  const closeQuoteModal = () => {
    setShowQuoteModal(false)
    setSelectedSupplier(null)
  }

  const openTargetPriceModal = () => {
    const suppliersForGate =
      Array.isArray(rfq?.suppliers) && rfq.suppliers.length > 0
        ? rfq.suppliers
        : rfq?.supplierMailId
          ? [
              {
                supplierName: rfq.supplierName || '-',
                supplierMailId: rfq.supplierMailId,
                phoneNumber: rfq.phoneNumber || ''
              }
            ]
          : []
    if (!rfqHasAnySupplierQuote(rfq, suppliersForGate)) {
      toast.info(
        'Price Matching is available after at least one supplier submits a quote',
        { autoClose: 2500 }
      )
      return
    }
    if (targetPriceModalCloseTimerRef.current) {
      clearTimeout(targetPriceModalCloseTimerRef.current)
      targetPriceModalCloseTimerRef.current = null
    }
    const current =
      rfq?.targetPrice != null &&
      rfq.targetPrice !== '' &&
      Number(rfq.targetPrice) > 0
        ? String(rfq.targetPrice)
        : ''
    setTargetPriceInput(current)
    setPriceMatchApplyTo('all')
    setPriceMatchSupplierMailId('')
    setBoqTargetInputs(buildBoqTargetInputsFromRfq(rfq, null))
    const hasTargets = rfqHasSavedPriceMatchingTargets(rfq, suppliersForGate)
    // Without saved targets, open Edit only — do not list Quote History yet.
    setShowTargetPriceSuppliers(hasTargets)
    setTargetPriceModalTab(hasTargets ? 'history' : 'edit')
    setShowTargetPriceModal(true)
    setTargetPriceModalAnimating(false)
    // Next frame: add `.show` so Bootstrap fade/slide transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTargetPriceModalAnimating(true)
      })
    })
  }

  const closeTargetPriceModal = (options = {}) => {
    if (isSavingTargetPrice && !options.force) return
    setTargetPriceModalAnimating(false)
    if (targetPriceModalCloseTimerRef.current) {
      clearTimeout(targetPriceModalCloseTimerRef.current)
    }
    targetPriceModalCloseTimerRef.current = setTimeout(() => {
      setShowTargetPriceModal(false)
      setShowTargetPriceSuppliers(false)
      setTargetPriceModalTab('history')
      setTargetPriceInput('')
      setPriceMatchApplyTo('all')
      setPriceMatchSupplierMailId('')
      setBoqTargetInputs({})
      targetPriceModalCloseTimerRef.current = null
    }, 200)
  }

  const handleBoqTargetInputChange = (key, value) => {
    if (!isDecimalInput(value)) return
    setBoqTargetInputs((prev) => ({ ...prev, [key]: value }))
  }

  const handlePriceMatchApplyToChange = (nextApplyTo) => {
    setPriceMatchApplyTo(nextApplyTo)
    if (nextApplyTo === 'all') {
      setPriceMatchSupplierMailId('')
      setBoqTargetInputs(buildBoqTargetInputsFromRfq(rfq, null))
      return
    }
    const firstQuoted = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) => supplierHasQuotedPrice(s) && String(s.supplierMailId || '').trim()
    )
    const mail = String(firstQuoted?.supplierMailId || '').trim()
    setPriceMatchSupplierMailId(mail)
    const selected = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) =>
        String(s.supplierMailId || '').trim().toLowerCase() === mail.toLowerCase()
    )
    setBoqTargetInputs(buildBoqTargetInputsFromRfq(rfq, selected || null))
  }

  const handlePriceMatchSupplierChange = (mailId) => {
    const mail = String(mailId || '').trim()
    setPriceMatchSupplierMailId(mail)
    const selected = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) =>
        String(s.supplierMailId || '').trim().toLowerCase() === mail.toLowerCase()
    )
    setBoqTargetInputs(buildBoqTargetInputsFromRfq(rfq, selected || null))
  }

  const refreshRfqAfterTargetPrice = async () => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-by-id',
        rfqid: id
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) return null
      const row = pickRfqFromResponse(responseData, id)
      if (row) {
        setRfq(row)
        return row
      }
    } catch (error) {
      console.error('Refresh RFQ after target price error:', error)
    }
    return null
  }

  useEffect(() => {
    return () => {
      if (targetPriceModalCloseTimerRef.current) {
        clearTimeout(targetPriceModalCloseTimerRef.current)
      }
    }
  }, [])

  const handleTargetPriceInputChange = (value) => {
    if (!isDecimalInput(value)) return
    setTargetPriceInput(value)
  }

  const handleSaveTargetPrice = async () => {
    if (!id || isSavingTargetPrice) return

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    const boqRows = collectRfqBoqTargetRows(rfq)
    const boqTargetPrices = []
    for (const row of boqRows) {
      const raw = String(boqTargetInputs[row.key] ?? '').trim()
      if (!raw) continue
      const parsedBoq = parseDecimalValue(raw)
      if (!Number.isFinite(parsedBoq) || parsedBoq < 0) {
        toast.error(
          `Enter a valid target for BOQ ${row.boqNo || row.key}`,
          { autoClose: 2200 }
        )
        return
      }
      const item = { targetPrice: parsedBoq }
      if (row.boq_id) item.boq_id = row.boq_id
      if (row.boqNo) item.boqNo = row.boqNo
      boqTargetPrices.push(item)
    }

    const totalRaw = String(targetPriceInput || '').trim()
    const parsedTotal = totalRaw ? parseDecimalValue(totalRaw) : NaN
    if (totalRaw && (!Number.isFinite(parsedTotal) || parsedTotal < 0)) {
      toast.error('Enter a valid RFQ total price matching value', {
        autoClose: 2000
      })
      return
    }

    const applyTo = priceMatchApplyTo === 'supplier' ? 'supplier' : 'all'

    if (applyTo === 'supplier') {
      if (!String(priceMatchSupplierMailId || '').trim()) {
        toast.error('Select a supplier for price matching', { autoClose: 2000 })
        return
      }
      if (boqTargetPrices.length === 0) {
        toast.error('Enter at least one BOQ target price for this supplier', {
          autoClose: 2200
        })
        return
      }
    } else if (!totalRaw && boqTargetPrices.length === 0) {
      toast.error(
        'Enter an RFQ total and/or at least one BOQ target price',
        { autoClose: 2200 }
      )
      return
    }

    const payload = {
      action: 'update-rfq-target-price',
      id,
      applyTo,
      currentUserId
    }

    if (applyTo === 'all' && totalRaw && Number.isFinite(parsedTotal)) {
      payload.targetPrice = parsedTotal
    }
    if (boqTargetPrices.length > 0) {
      payload.boqTargetPrices = boqTargetPrices
    }
    if (applyTo === 'supplier') {
      payload.supplierMailId = String(priceMatchSupplierMailId).trim()
    }

    setIsSavingTargetPrice(true)
    try {
      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || 'Failed to update price matching value',
          { autoClose: 2500 }
        )
        setIsSavingTargetPrice(false)
        return
      }

      const nextTarget =
        responseData?.data?.targetPrice != null
          ? responseData.data.targetPrice
          : applyTo === 'all' && Number.isFinite(parsedTotal)
            ? parsedTotal
            : rfq?.targetPrice

      const dataBlock =
        responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : {}

      setRfq((prev) =>
        prev
          ? {
              ...prev,
              ...dataBlock,
              ...(nextTarget != null ? { targetPrice: nextTarget } : {}),
              updatedAt: dataBlock.updatedAt || prev.updatedAt
            }
          : prev
      )

      await refreshRfqAfterTargetPrice()

      const appliedList = Array.isArray(dataBlock.appliedToSuppliers)
        ? dataBlock.appliedToSuppliers
        : []
      const skippedList = Array.isArray(dataBlock.skippedSuppliers)
        ? dataBlock.skippedSuppliers
        : []
      const appliedCount =
        Number(dataBlock.appliedToSuppliersCount) ||
        Number(dataBlock.appliedCount) ||
        appliedList.length
      const skippedCount =
        Number(dataBlock.skippedSuppliersCount) ||
        Number(dataBlock.skippedCount) ||
        skippedList.length

      let successMsg =
        responseData?.message || 'Price matching value updated successfully'
      if (applyTo === 'all' && (appliedCount > 0 || skippedCount > 0)) {
        successMsg = `${successMsg} Applied to ${appliedCount} quoted supplier${
          appliedCount === 1 ? '' : 's'
        }; skipped ${skippedCount} without quote.`
      }

      toast.success(successMsg, { autoClose: 3500 })
      setShowTargetPriceSuppliers(true)
      setTargetPriceModalTab('notify')
      setIsSavingTargetPrice(false)
    } catch (error) {
      console.error('Update RFQ price matching error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message ||
          error.message ||
          'An error occurred while updating price matching value',
        { autoClose: 2500 }
      )
      setIsSavingTargetPrice(false)
    }
  }

  const downloadRfqOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return
    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl || fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const downloadSupplierAttachment = async (s3Key, fallbackUrl, supplierEmail) => {
    if (!s3Key || downloadingS3Key) return
    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-supplier-attachment-download-presigned-url',
        rfqNumber: rfq?.rfqNumber || rfq?.rfqNo || '',
        rfqid: id,
        email: String(supplierEmail || '').trim(),
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl || fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download supplier attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View RFQ</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/rfq">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!rfq) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>RFQ not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const materials = Array.isArray(rfq.materials) ? rfq.materials : []
  const rfqTermsRows = getRfqTermsRows(rfq)
  const suppliers =
    Array.isArray(rfq.suppliers) && rfq.suppliers.length > 0
      ? rfq.suppliers
      : rfq.supplierMailId
        ? [{ supplierName: rfq.supplierName || '-', supplierMailId: rfq.supplierMailId, phoneNumber: rfq.phoneNumber || '' }]
        : []
  const targetPriceHistory = getTargetPriceQuoteHistory(rfq)
  const canUsePriceMatching = rfqHasAnySupplierQuote(rfq, suppliers)
  const hasPriceMatchingValue =
    rfq.targetPrice != null &&
    rfq.targetPrice !== '' &&
    Number.isFinite(Number(rfq.targetPrice)) &&
    Number(rfq.targetPrice) > 0
  const hasAnyPriceMatchingTarget = rfqHasSavedPriceMatchingTargets(
    rfq,
    suppliers
  )
  const boqTargetRows = collectRfqBoqTargetRows(rfq)
  const priceMatchSupplierOptions = suppliers.filter(
    (s) => String(s.supplierMailId || '').trim() && supplierHasQuotedPrice(s)
  )
  const currencyLabel = rfq.rfqCurrency ? ` ${rfq.rfqCurrency}` : ''
  const rfqNo = rfq.rfqNumber || rfq.rfqNo || '-'

  const quotedSuppliersForPriceMatch = suppliers.filter((s) =>
    supplierHasQuotedPrice(s)
  )
  const unquotedSuppliersForPriceMatch = suppliers.filter(
    (s) => !supplierHasQuotedPrice(s)
  )
  const priceMatchingSuppliersList = getPriceMatchingSuppliersList(rfq, suppliers)
  const matchedSuppliersCount = quotedSuppliersForPriceMatch.filter(
    (s) =>
      !priceMatchingSuppliersList.some((pm) => suppliersMatchIdentity(pm, s))
  ).length
  const boqPriceMatchingLines = getResolvedBoqPriceMatchingLines(rfq, suppliers)
  const needsMatchingBoqLineCount = boqPriceMatchingLines.filter((line) => {
    if (line?.needsPriceMatching === true) return true
    if (line?.needsPriceMatching === false) return false
    return getBoqLineSupplierEntries(line).some(lineNeedsPriceMatching)
  }).length

  const renderPriceMatchingEditForm = (inputIdPrefix = 'rfqTargetPrice') => (
    <>
      <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
        Set price matching for{' '}
        <strong>{rfq?.rfqNumber || 'this RFQ'}</strong>
        {rfq?.rfqCurrency ? ` (${rfq.rfqCurrency})` : ''}. Apply the same BOQ
        targets to all <em>quoted</em> suppliers, or to one supplier only.
      </p>

      <div className="form-group mb-3">
        <label className="d-block mb-2">Apply To</label>
        <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
          <label className="mb-0" style={{ fontWeight: 500, cursor: 'pointer' }}>
            <input
              type="radio"
              className="mr-1"
              name={`${inputIdPrefix}-applyTo`}
              checked={priceMatchApplyTo === 'all'}
              onChange={() => handlePriceMatchApplyToChange('all')}
              disabled={isSavingTargetPrice}
            />
            All quoted suppliers
          </label>
          <label className="mb-0" style={{ fontWeight: 500, cursor: 'pointer' }}>
            <input
              type="radio"
              className="mr-1"
              name={`${inputIdPrefix}-applyTo`}
              checked={priceMatchApplyTo === 'supplier'}
              onChange={() => handlePriceMatchApplyToChange('supplier')}
              disabled={isSavingTargetPrice}
            />
            One supplier
          </label>
        </div>
      </div>

      {priceMatchApplyTo === 'supplier' ? (
        <div className="form-group mb-3">
          <label htmlFor={`${inputIdPrefix}-supplier`}>Supplier</label>
          <select
            id={`${inputIdPrefix}-supplier`}
            className="form-control form-control-sm"
            value={priceMatchSupplierMailId}
            onChange={(e) => handlePriceMatchSupplierChange(e.target.value)}
            disabled={isSavingTargetPrice}
          >
            <option value="">Select supplier</option>
            {(priceMatchSupplierOptions.length > 0
              ? priceMatchSupplierOptions
              : suppliers
            ).map((s) => (
              <option
                key={s.supplierId || s.supplierMailId}
                value={String(s.supplierMailId || '').trim()}
              >
                {s.supplierName || s.supplierMailId}
                {s.supplierMailId ? ` (${s.supplierMailId})` : ''}
                {supplierNeedsPriceMatching(s) ? ' — needs matching' : ''}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="form-group mb-3">
          <label htmlFor={`${inputIdPrefix}-total`}>
            RFQ Total Target (optional)
          </label>
          <input
            id={`${inputIdPrefix}-total`}
            type="text"
            className="form-control"
            inputMode="decimal"
            placeholder="0.00"
            value={targetPriceInput}
            onChange={(e) => handleTargetPriceInputChange(e.target.value)}
            disabled={isSavingTargetPrice}
          />
          <small className="form-text text-muted">
            Stored as RFQ <code>targetPrice</code>. Leave blank to update only
            BOQ line targets.
          </small>
        </div>
      )}

      {boqTargetRows.length === 0 ? (
        <div className="alert alert-light border mb-0" style={{ fontSize: '13px' }}>
          No BOQ lines on this RFQ. Set an RFQ total target for all suppliers.
        </div>
      ) : (
        <div className="table-responsive mb-0">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px' }}>BOQ No</th>
                <th style={{ fontSize: '12px' }}>Description</th>
                <th style={{ fontSize: '12px' }}>Qty</th>
                <th style={{ fontSize: '12px', minWidth: '120px' }}>
                  Target Price
                </th>
              </tr>
            </thead>
            <tbody>
              {boqTargetRows.map((row) => (
                <tr key={row.key} style={{ backgroundColor: '#fff8e6' }}>
                  <td className="fwt_600" style={{ color: '#856404' }}>
                    {row.boqNo}
                  </td>
                  <td
                    style={{
                      maxWidth: '240px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.materialDescription}
                    {row.parentDescription ? (
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        {row.parentDescription}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {row.quantity ?? '-'}
                    {row.uom ? ` ${row.uom}` : ''}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={boqTargetInputs[row.key] ?? ''}
                      onChange={(e) =>
                        handleBoqTargetInputChange(row.key, e.target.value)
                      }
                      disabled={isSavingTargetPrice}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="RFQ Details" />

                    <ViewField label="RFQ Number">{rfqNo}</ViewField>
                    <ViewField label="RFQ Date">{formatDate(rfq.rfqDate)}</ViewField>
                    <ViewField label="RFQ Due Date">{formatDate(rfq.rfqDueDate)}</ViewField>
                    <ViewField label="Currency">{rfq.rfqCurrency || '-'}</ViewField>
                    <ViewField label="RFQ Status">
                      <span className="fwt_600">{capitalizeStatus(rfq.rfqStatus)}</span>
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${
                          String(rfq.status).toLowerCase() === 'active'
                            ? 'badge-success'
                            : 'badge-secondary'
                        }`}
                        style={{ fontSize: '90%', fontWeight: 550 }}
                      >
                        {capitalizeStatus(rfq.status)}
                      </span>
                    </ViewField>
                    <ViewField label="RFQ Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{rfq.rfqSummary || '-'}</span>
                    </ViewField>

                    <SectionTitle icon="fa-list-check" title="Terms" />

                    {rfqTermsRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '160px' }}>RFQ Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Terms Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rfqTermsRows.map((term, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td
                                  style={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.rfqTerm}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.termsResponse}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Material Code</th>
                              <th style={{ fontSize: '12px' }}>Material Description</th>
                              <th style={{ fontSize: '12px' }}>PR Number</th>
                              <th style={{ fontSize: '12px' }}>HSN</th>
                              <th style={{ fontSize: '12px' }}>UOM</th>
                              <th style={{ fontSize: '12px' }}>Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.flatMap((row, index) => {
                              const lineNo = row.lineNo != null ? row.lineNo : index + 1
                              const parentRow = (
                                <tr key={`mat-${index}`}>
                                  <td>
                                    {lineNo}
                                    {row.isBoq ? (
                                      <span
                                        className="badge badge-warning ml-1"
                                        style={{ fontSize: '9px' }}
                                      >
                                        BOQ
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{row.materialCode || '-'}</td>
                                  <td
                                    style={{
                                      maxWidth: '240px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {row.materialDescription || '-'}
                                  </td>
                                  <td>{row.prNumber || row.prNo || '-'}</td>
                                  <td>{row.hsn || row.hsnCode || '-'}</td>
                                  <td>{row.uom || '-'}</td>
                                  <td>{row.quantity ?? '-'}</td>
                                </tr>
                              )

                              const boqRows =
                                row.isBoq && Array.isArray(row.boq)
                                  ? row.boq.map((boqItem, boqIndex) => {
                                      const boqNo =
                                        boqItem.boqNo ||
                                        `${lineNo}.${boqIndex + 1}`
                                      return (
                                        <tr
                                          key={`mat-${index}-boq-${boqItem._id || boqIndex}`}
                                          style={{ backgroundColor: '#fff8e6' }}
                                        >
                                          <td
                                            style={{
                                              fontWeight: 600,
                                              color: '#856404',
                                              paddingLeft: '14px'
                                            }}
                                          >
                                            {boqNo}
                                          </td>
                                          <td>{boqItem.materialCode || '-'}</td>
                                          <td
                                            style={{
                                              maxWidth: '240px',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {boqItem.materialDescription || '-'}
                                          </td>
                                          <td>
                                            {boqItem.prNumber ||
                                              boqItem.prNo ||
                                              row.prNumber ||
                                              row.prNo ||
                                              '-'}
                                          </td>
                                          <td>
                                            {boqItem.hsn ||
                                              boqItem.hsnCode ||
                                              '-'}
                                          </td>
                                          <td>{boqItem.uom || '-'}</td>
                                          <td>{boqItem.quantity ?? '-'}</td>
                                        </tr>
                                      )
                                    })
                                  : []

                              return [parentRow, ...boqRows]
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-envelope" title="Supplier Email" />

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group mb-2">
                          <label>CC Emails (your email)</label>
                          <div className="txtdsply" style={{ fontSize: '13px' }}>
                            {userEmail || (
                              <span className="text-muted">
                                Profile email not available — add CC addresses below
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-2" style={{ position: 'relative' }}>
                          <label>Additional CC Emails</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Type 3+ letters to search users"
                            value={additionalCcEmails}
                            onChange={handleAdditionalCcChange}
                            onBlur={handleAdditionalCcBlur}
                            disabled={Boolean(resendingFor)}
                            autoComplete="off"
                          />
                          {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1060,
                                backgroundColor: '#fff',
                                border: '1px solid #d8e0ea',
                                borderRadius: '8px',
                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                maxHeight: '210px',
                                overflowY: 'auto',
                                marginTop: '2px'
                              }}
                            >
                              {isSearchingCcUsers ? (
                                <div
                                  className="text-muted"
                                  style={{ padding: '10px 12px', fontSize: '13px' }}
                                >
                                  <i className="fa-solid fa-spinner fa-spin mr-2" />
                                  Searching users...
                                </div>
                              ) : (() => {
                                const existing = new Set(
                                  buildCcEmails(userEmail, additionalCcEmails).map(
                                    (item) => item.toLowerCase()
                                  )
                                )
                                const visible = ccUserSuggestions.filter(
                                  (user) =>
                                    !existing.has(String(user.email).trim().toLowerCase())
                                )
                                if (visible.length === 0) {
                                  return (
                                    <div
                                      className="text-muted"
                                      style={{ padding: '10px 12px', fontSize: '13px' }}
                                    >
                                      No matching users found
                                    </div>
                                  )
                                }
                                return visible.map((user) => (
                                  <button
                                    key={user.id || user.email}
                                    type="button"
                                    className="dropdown-item"
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      textAlign: 'left',
                                      padding: '8px 12px',
                                      whiteSpace: 'normal',
                                      cursor: 'pointer'
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      handleSelectCcUser(user.email)
                                    }}
                                  >
                                    <span
                                      className="d-block fwt_600"
                                      style={{ fontSize: '13px' }}
                                    >
                                      {user.name || user.email}
                                    </span>
                                    <small className="text-muted">{user.email}</small>
                                  </button>
                                ))
                              })()}
                            </div>
                          )}
                          <small className="form-text text-muted">
                           select a user, then continue after
                            the comma for the next email.
                          </small>
                          {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                            <small className="form-text text-muted d-block">
                              Will CC: {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    {suppliers.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No suppliers</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Supplier Name</th>
                              <th style={{ fontSize: '12px' }}>Email</th>
                              <th style={{ fontSize: '12px' }}>City</th>
                              <th style={{ fontSize: '12px' }}>Phone Number</th>
                              <th style={{ fontSize: '12px', width: '90px' }}>Link</th>
                              <th style={{ fontSize: '12px', width: '90px' }}>View</th>
                              <th style={{ fontSize: '12px', width: '100px' }}>Resend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suppliers.map((row, index) => {
                              const viewBtn = getSupplierViewButtonProps(row)
                              const supplierEmail = String(row.supplierMailId || '').trim()
                              const isResending = resendingFor === supplierEmail
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      maxWidth: '220px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {row.supplierName || '-'}
                                    {supplierNeedsPriceMatching(row) ? (
                                      <span
                                        className="badge badge-warning ml-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        Needs matching
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{row.supplierMailId || '-'}</td>
                                  <td>{row.city || '-'}</td>
                                  <td>{row.phoneNumber || row.phonenumber || '-'}</td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      title="Copy supplier link"
                                      onClick={() => handleCopySupplierLink(row)}
                                      disabled={!getSupplierEmailLink(id, row)}
                                    >
                                      <i className="fa-solid fa-copy" />
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className={viewBtn.className}
                                      title={viewBtn.title}
                                      onClick={() => openQuoteModal(row)}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      title="Resend RFQ email to supplier"
                                      onClick={() => handleResendEmail(row)}
                                      disabled={
                                        !supplierEmail ||
                                        isResending ||
                                        String(rfq.status).toLowerCase() !== 'active' ||
                                        !rfq.pdfLink?.trim()
                                      }
                                    >
                                      {isResending ? (
                                        <i className="fa-solid fa-spinner fa-spin" />
                                      ) : (
                                        <i className="fa-solid fa-paper-plane" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <ViewField label="Mail Sent">
                      {rfq.isMailSent ? (
                        <span className="badge badge-success">Yes</span>
                      ) : (
                        <span className="badge badge-secondary">No</span>
                      )}
                    </ViewField>

                    <ViewField label="Comparison">
                      <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                        <Link to={`/rfqComparison/${id}`} className="btn btn-blue btn-sm">
                          <i className="fa-solid fa-chart-column mr-1" />
                          View Comparison
                        </Link>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={openTargetPriceModal}
                          disabled={!canUsePriceMatching}
                          title={
                            canUsePriceMatching
                              ? 'Set or update price matching value'
                              : 'Price Matching is available after at least one supplier submits a quote'
                          }
                        >
                          <i className="fa-solid fa-bullseye mr-1" />
                          Price Matching
                          {hasPriceMatchingValue ? (
                            <span className="ml-1">
                              ({formatAmount(rfq.targetPrice)}
                              {rfq.rfqCurrency ? ` ${rfq.rfqCurrency}` : ''})
                            </span>
                          ) : null}
                        </button>
                      </div>
                    </ViewField>

                    {(rfq.pdfLink || rfqOwnerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {rfq.pdfLink ? (
                          <ViewField label="PDF Link">
                            <a href={rfq.pdfLink} target="_blank" rel="noopener noreferrer">
                              View PDF
                            </a>
                          </ViewField>
                        ) : null}

                        {rfqOwnerAttachments.length > 0 ? (
                          <ViewField label="RFQ Owner Attachments">
                            <AttachmentTileGrid
                              attachments={rfqOwnerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadRfqOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">{displayUser(rfq.createdBy)}</ViewField>
                    <ViewField label="Updated By">{displayUser(rfq.updatedBy)}</ViewField>
                    <ViewField label="Created At">{formatDateTime(rfq.createdAt)}</ViewField>
                    <ViewField label="Updated At">{formatDateTime(rfq.updatedAt)}</ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showQuoteModal && selectedSupplier && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex="-1"
          role="dialog"
          onClick={closeQuoteModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Supplier Quote — {selectedSupplier.supplierName || '-'}
                  {selectedSupplier._historyPhase ? (
                    <span
                      className={`badge ml-2 ${
                        String(selectedSupplier._historyPhase).includes('before')
                          ? 'badge-warning'
                          : 'badge-success'
                      }`}
                      style={{ fontSize: '11px', fontWeight: 500 }}
                    >
                      {String(selectedSupplier._historyPhase)
                        .replace(/_/g, ' ')}
                    </span>
                  ) : null}
                </h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={closeQuoteModal}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Email:</strong>{' '}
                    {selectedSupplier.supplierMailId || '-'}
                  </div>
                  <div className="col-md-6 text-md-right">
                    <strong>Submitted At:</strong>{' '}
                    {formatDateTime(selectedSupplier.quoteSubmittedAt)}
                  </div>
                </div>

                {hasSupplierQuotes(selectedSupplier) ? (
                  <>
                    {selectedSupplier.materialQuotes?.length > 0 ? (
                      <>
                        <h6 className="text-primary mb-2">
                          <i className="fa-solid fa-boxes-stacked mr-1" />
                          Materials
                        </h6>
                        <div className="table-responsive mb-4" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                <th style={{ fontSize: '12px' }}>S.No</th>
                                <th style={{ fontSize: '12px' }}>Material Code</th>
                                <th style={{ fontSize: '12px', minWidth: '200px' }}>Material Description</th>
                                <th style={{ fontSize: '12px', minWidth: '80px' }}>HSN</th>
                                <th style={{ fontSize: '12px' }}>UOM</th>
                                <th style={{ fontSize: '12px' }}>Qty</th>
                                <th style={{ fontSize: '12px' }}>Basic Price</th>
                                <th style={{ fontSize: '12px' }}>GST %</th>
                                <th style={{ fontSize: '12px' }}>GST Value</th>
                                <th style={{ fontSize: '12px' }}>Total Value</th>
                                <th style={{ fontSize: '12px', minWidth: '140px' }}>Technical Response</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedSupplier.materialQuotes.flatMap((quote, index) => {
                                const lineNo =
                                  quote.lineNo != null ? quote.lineNo : index + 1
                                const hasBoq =
                                  quote.isBoq &&
                                  Array.isArray(quote.boq) &&
                                  quote.boq.length > 0

                                const parentRow = (
                                  <tr key={`quote-${index}`}>
                                    <td>
                                      {lineNo}
                                      {hasBoq ? (
                                        <span
                                          className="badge badge-warning ml-1"
                                          style={{ fontSize: '9px' }}
                                        >
                                          BOQ
                                        </span>
                                      ) : null}
                                    </td>
                                    <td>{quote.materialCode || '-'}</td>
                                    <td
                                      style={{
                                        maxWidth: '220px',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {quote.materialDescription || '-'}
                                    </td>
                                    <td>{displayQuoteHsn(quote)}</td>
                                    <td>{quote.uom || '-'}</td>
                                    <td>{quote.quantity ?? '-'}</td>
                                    <td className="text-right fwt_600">
                                      {formatAmount(quote.basicPrice)}
                                    </td>
                                    <td className="text-right">
                                      {formatAmount(quote.gstPercent)}
                                    </td>
                                    <td className="text-right">
                                      {formatAmount(quote.gstValue)}
                                    </td>
                                    <td className="text-right fwt_600">
                                      {formatAmount(quote.totalValue)}
                                    </td>
                                    <td
                                      style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {quote.comments || '-'}
                                    </td>
                                  </tr>
                                )

                                const boqRows = hasBoq
                                  ? quote.boq.map((boqItem, boqIndex) => {
                                      const boqNo =
                                        boqItem.boqNo ||
                                        `${lineNo}.${boqIndex + 1}`
                                      return (
                                        <tr
                                          key={`quote-${index}-boq-${boqItem.boq_id || boqItem._id || boqIndex}`}
                                          style={{ backgroundColor: '#fff8e6' }}
                                        >
                                          <td
                                            style={{
                                              fontWeight: 600,
                                              color: '#856404',
                                              paddingLeft: '14px'
                                            }}
                                          >
                                            {boqNo}
                                          </td>
                                          <td>{boqItem.materialCode || '-'}</td>
                                          <td
                                            style={{
                                              maxWidth: '220px',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {boqItem.materialDescription || '-'}
                                          </td>
                                          <td>
                                            {boqItem.hsn ||
                                              boqItem.hsnCode ||
                                              '-'}
                                          </td>
                                          <td>{boqItem.uom || '-'}</td>
                                          <td>{boqItem.quantity ?? '-'}</td>
                                          <td className="text-right">
                                            {formatAmount(boqItem.basicPrice)}
                                          </td>
                                          <td className="text-center text-muted">
                                            —
                                          </td>
                                          <td className="text-center text-muted">
                                            —
                                          </td>
                                          <td className="text-center text-muted">
                                            —
                                          </td>
                                          <td className="text-center text-muted">
                                            —
                                          </td>
                                        </tr>
                                      )
                                    })
                                  : []

                                return [parentRow, ...boqRows]
                              })}
                              <tr style={{ backgroundColor: '#f4f6f9' }}>
                                <td colSpan={6} className="text-right fwt_600">
                                  Gross Total
                                  {rfq.rfqCurrency ? ` (${rfq.rfqCurrency})` : ''}
                                </td>
                                <td className="text-right fwt_600">
                                  {formatAmount(
                                    selectedSupplier.grossTotalBasicPrice
                                  )}
                                </td>
                                <td />
                                <td className="text-right fwt_600">
                                  {formatAmount(selectedSupplier.grossGstValue)}
                                </td>
                                <td className="text-right fwt_600">
                                  {formatAmount(selectedSupplier.grossTotalValue)}
                                </td>
                                <td />
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}

                    {getSupplierTerms(selectedSupplier).length > 0 ? (
                      <>
                        <h6 className="text-primary mb-2">
                          <i className="fa-solid fa-list-check mr-1" />
                          Terms
                        </h6>
                        <div className="table-responsive" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>RFQ Term</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>Terms Response</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>Quotes Response</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getSupplierTerms(selectedSupplier).map((term, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.rfqTerm || '-'}
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.termsResponse || '-'}
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.comments || term.termsComments || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : (
                  <p className="text-muted mb-0">No quotation submitted yet.</p>
                )}

                {getSupplierAttachments(selectedSupplier).length > 0 ? (
                  <>
                    <h6 className="text-primary mb-2 mt-3">
                      <i className="fa-solid fa-paperclip mr-1" />
                      Attachments
                    </h6>
                    <AttachmentTileGrid
                      attachments={getSupplierAttachments(selectedSupplier)}
                      downloadingS3Key={downloadingS3Key}
                      onDownload={(s3Key, url) =>
                        downloadSupplierAttachment(
                          s3Key,
                          url,
                          selectedSupplier.supplierMailId
                        )
                      }
                      disabled={Boolean(downloadingS3Key)}
                    />
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeQuoteModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showQuoteModal && <div className="modal-backdrop fade show" />}

      {showTargetPriceModal ? (
        <div
          className={`modal fade${targetPriceModalAnimating ? ' show' : ''}`}
          style={{ display: 'block' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`modal-dialog modal-dialog-centered modal-dialog-scrollable${
              showTargetPriceSuppliers ? ' modal-xl' : ''
            }`}
            role="document"
            style={{
              transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
              transform: targetPriceModalAnimating
                ? 'none'
                : 'translate(0, -30px)',
              opacity: targetPriceModalAnimating ? 1 : 0
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0">
                  <i className="fa-solid fa-bullseye mr-2 text-primary" />
                  {showTargetPriceSuppliers
                    ? 'Price Matching & Quote History'
                    : 'Set Price Matching'}
                </h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={() => closeTargetPriceModal()}
                  disabled={isSavingTargetPrice}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: '75vh', overflowY: 'auto' }}
              >
                {!showTargetPriceSuppliers ? (
                  renderPriceMatchingEditForm('rfqTargetPrice')
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-4 mb-2 mb-md-0">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#eef6fb',
                            borderRadius: '8px',
                            border: '1px solid #d0e4f0'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            Price Matching
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {formatAmount(
                              targetPriceHistory.targetPrice ??
                                rfq?.targetPrice ??
                                targetPriceInput
                            )}
                            {currencyLabel}
                          </div>
                          {boqPriceMatchingLines.length > 0 ? (
                            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                              {needsMatchingBoqLineCount} of{' '}
                              {boqPriceMatchingLines.length} BOQ line
                              {boqPriceMatchingLines.length === 1 ? '' : 's'} still
                              above target
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-4 mb-2 mb-md-0">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#fff8e6',
                            borderRadius: '8px',
                            border: '1px solid #f0e0b2'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            Needs Matching
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {priceMatchingSuppliersList.length}{' '}
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              supplier
                              {priceMatchingSuppliersList.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                            Before history:{' '}
                            {targetPriceHistory.suppliersWithBeforeCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#e8f7f0',
                            borderRadius: '8px',
                            border: '1px solid #bfe8d4'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            At / Below Target
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {matchedSuppliersCount}{' '}
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              supplier
                              {matchedSuppliersCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                            After history:{' '}
                            {targetPriceHistory.suppliersWithAfterCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <ul className="nav nav-pills mb-3" style={{ gap: '6px' }}>
                      {hasAnyPriceMatchingTarget ? (
                        <li className="nav-item">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              targetPriceModalTab === 'history'
                                ? 'btn-blue'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() => {
                              setTargetPriceModalTab('history')
                            }}
                          >
                            <i className="fa-solid fa-clock-rotate-left mr-1" />
                            Quote History
                            {priceMatchingSuppliersList.length > 0 ? (
                              <span className="badge badge-warning ml-1">
                                {priceMatchingSuppliersList.length}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      ) : null}
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            targetPriceModalTab === 'notify'
                              ? 'btn-blue'
                              : 'btn-outline-secondary'
                          }`}
                          onClick={() => {
                            setTargetPriceModalTab('notify')
                          }}
                        >
                          <i className="fa-solid fa-paper-plane mr-1" />
                          Notify Suppliers
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            targetPriceModalTab === 'edit'
                              ? 'btn-blue'
                              : 'btn-outline-secondary'
                          }`}
                          onClick={() => {
                            setTargetPriceModalTab('edit')
                          }}
                        >
                          <i className="fa-solid fa-pen-to-square mr-1" />
                          Edit Value
                        </button>
                      </li>
                    </ul>

                    {targetPriceModalTab === 'edit' ? (
                      renderPriceMatchingEditForm('rfqTargetPriceEdit')
                    ) : targetPriceModalTab === 'notify' ? (
                      suppliers.length === 0 ? (
                        <div className="txtdsply text-muted mb-0">
                          No suppliers assigned to this RFQ.
                        </div>
                      ) : (
                        <>
                          <div
                            className="alert alert-info py-2 mb-3"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fa-solid fa-users mr-1" />
                            All assigned suppliers (
                            <strong>{suppliers.length}</strong>
                            ). Status shows who still needs matching vs already
                            at/below target.
                          </div>
                          <div className="table-responsive mb-0">
                            <table className="table table-bordered table-sm mb-0">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: '#003366',
                                    color: '#fff'
                                  }}
                                >
                                  <th style={{ fontSize: '12px' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>
                                    Supplier Name
                                  </th>
                                  <th style={{ fontSize: '12px' }}>Email</th>
                                  <th style={{ fontSize: '12px' }}>City</th>
                                  <th style={{ fontSize: '12px' }}>
                                    Phone Number
                                  </th>
                                  <th style={{ fontSize: '12px', width: '110px' }}>
                                    Status
                                  </th>
                                  <th
                                    style={{ fontSize: '12px', width: '100px' }}
                                  >
                                    Send
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {suppliers.map((row, index) => {
                                  const supplierEmail = String(
                                    row.supplierMailId || ''
                                  ).trim()
                                  const isSending =
                                    sendingPriceMatchFor === supplierEmail
                                  const needsMatching =
                                    priceMatchingSuppliersList.some((pm) =>
                                      suppliersMatchIdentity(pm, row)
                                    ) || supplierNeedsPriceMatching(row)
                                  const hasQuoted = supplierHasQuotedPrice(row)
                                  return (
                                    <tr
                                      key={
                                        row.supplierId || supplierEmail || index
                                      }
                                    >
                                      <td>{index + 1}</td>
                                      <td
                                        style={{
                                          maxWidth: '220px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {row.supplierName || '-'}
                                      </td>
                                      <td>{row.supplierMailId || '-'}</td>
                                      <td>{row.city || '-'}</td>
                                      <td>
                                        {row.phoneNumber ||
                                          row.phonenumber ||
                                          '-'}
                                      </td>
                                      <td className="text-center">
                                        {!hasQuoted ? (
                                          <span className="badge badge-secondary">
                                            No quote
                                          </span>
                                        ) : needsMatching ? (
                                          <span className="badge badge-warning">
                                            Needs matching
                                          </span>
                                        ) : (
                                          <span className="badge badge-success">
                                            At / below
                                          </span>
                                        )}
                                      </td>
                                      <td className="text-center">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          title="Send price matching email"
                                          onClick={() =>
                                            handleSendPriceMatchingEmail(row)
                                          }
                                          disabled={
                                            !supplierEmail ||
                                            Boolean(sendingPriceMatchFor)
                                          }
                                        >
                                          {isSending ? (
                                            <i className="fa-solid fa-spinner fa-spin" />
                                          ) : (
                                            <i className="fa-solid fa-paper-plane" />
                                          )}
                                        </button>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )
                    ) : !hasAnyPriceMatchingTarget ? (
                      <div className="txtdsply text-muted mb-0">
                        No target price set yet. Use Edit Value to add price
                        matching targets first.
                      </div>
                    ) : boqPriceMatchingLines.length === 0 ? (
                      <div className="txtdsply text-muted mb-0">
                        No BOQ quoted vs target data yet. Set price matching
                        targets and wait for supplier quotes.
                      </div>
                    ) : (
                      <div>
                        <div
                          className="px-3 py-2 fwt_600 d-flex justify-content-between align-items-center"
                          style={{
                            background: '#eef6fb',
                            border: '1px solid #d0e4f0',
                            borderBottom: 'none',
                            borderRadius: '8px 8px 0 0',
                            fontSize: '13px'
                          }}
                        >
                          <span>
                            <i className="fa-solid fa-table mr-1" />
                            BOQ Quoted vs Target
                            <span
                              className="text-muted ml-2"
                              style={{ fontSize: '12px', fontWeight: 500 }}
                            >
                              (final revision)
                            </span>
                          </span>
                          <span
                            className="text-muted"
                            style={{ fontSize: '12px', fontWeight: 500 }}
                          >
                            {needsMatchingBoqLineCount} line
                            {needsMatchingBoqLineCount === 1 ? '' : 's'} need
                            matching
                          </span>
                        </div>
                        <div
                          className="table-responsive"
                          style={{
                            border: '1px solid #d0e4f0',
                            borderRadius: '0 0 8px 8px',
                            maxHeight: '520px',
                            overflowY: 'auto'
                          }}
                        >
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: '#003366',
                                  color: '#fff'
                                }}
                              >
                                <th style={{ fontSize: '12px' }}>BOQ</th>
                                <th style={{ fontSize: '12px' }}>Description</th>
                                <th style={{ fontSize: '12px' }}>Supplier</th>
                                <th style={{ fontSize: '12px' }}>
                                  Before Quoted Price
                                </th>
                                <th style={{ fontSize: '12px' }}>Target Price</th>
                                <th style={{ fontSize: '12px' }}>Quoted</th>
                                <th style={{ fontSize: '12px' }}>Difference</th>
                                <th style={{ fontSize: '12px' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {boqPriceMatchingLines.flatMap((line, lineIdx) => {
                                const supplierEntries =
                                  getBoqLineSupplierEntries(line)
                                const entries =
                                  supplierEntries.length > 0
                                    ? supplierEntries
                                    : [null]
                                const lineTarget =
                                  getLineEffectiveTargetPrice(line)
                                return entries.map((entry, entryIdx) => {
                                  const quoted = entry
                                    ? getLineQuotedPrice(entry)
                                    : NaN
                                  const target = entry
                                    ? getLineEffectiveTargetPrice(entry)
                                    : lineTarget
                                  const effectiveTarget = Number.isFinite(target)
                                    ? target
                                    : lineTarget
                                  const beforeQuoted = getEntryBeforeQuotedPrice(
                                    entry,
                                    line,
                                    rfq
                                  )
                                  const diff = entry
                                    ? getLinePriceDifference({
                                        ...entry,
                                        targetPrice:
                                          entry.targetPrice ?? effectiveTarget
                                      })
                                    : NaN
                                  const needsMatch = entry
                                    ? lineNeedsPriceMatching({
                                        ...entry,
                                        targetPrice:
                                          entry.targetPrice ?? effectiveTarget
                                      })
                                    : false
                                  return (
                                    <tr
                                      key={`${line.boqNo || lineIdx}-${entry?.supplierId || entry?.supplierMailId || entryIdx}`}
                                      style={{
                                        backgroundColor: needsMatch
                                          ? '#fff8e6'
                                          : entryIdx === 0
                                            ? '#fff'
                                            : '#fafbfc'
                                      }}
                                    >
                                      {entryIdx === 0 ? (
                                        <>
                                          <td
                                            rowSpan={entries.length}
                                            className="fwt_600 align-middle"
                                            style={{ color: '#856404' }}
                                          >
                                            {line.boqNo || '-'}
                                          </td>
                                          <td
                                            rowSpan={entries.length}
                                            className="align-middle"
                                            style={{
                                              maxWidth: '180px',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {line.materialDescription ||
                                              line.description ||
                                              '-'}
                                          </td>
                                        </>
                                      ) : null}
                                      <td>
                                        {entry?.supplierName ||
                                          entry?.supplierMailId ||
                                          '-'}
                                      </td>
                                      <td className="text-right">
                                        {Number.isFinite(beforeQuoted)
                                          ? formatAmount(beforeQuoted)
                                          : '-'}
                                      </td>
                                      <td className="text-right fwt_600 text-primary">
                                        {Number.isFinite(effectiveTarget)
                                          ? formatAmount(effectiveTarget)
                                          : Number.isFinite(lineTarget)
                                            ? formatAmount(lineTarget)
                                            : '-'}
                                      </td>
                                      <td className="text-right fwt_600">
                                        {Number.isFinite(quoted)
                                          ? formatAmount(quoted)
                                          : '-'}
                                      </td>
                                      <td
                                        className="text-right fwt_600"
                                        style={{
                                          color: needsMatch
                                            ? '#c82333'
                                            : Number.isFinite(diff) && diff < 0
                                              ? '#1e7e34'
                                              : '#6c757d'
                                        }}
                                      >
                                        {Number.isFinite(diff)
                                          ? formatSignedAmount(diff)
                                          : '-'}
                                      </td>
                                      <td className="text-center">
                                        {entry ? (
                                          needsMatch ? (
                                            <span className="badge badge-warning">
                                              Needs matching
                                            </span>
                                          ) : (
                                            <span className="badge badge-success">
                                              At / below
                                            </span>
                                          )
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {!showTargetPriceSuppliers || targetPriceModalTab === 'edit' ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() =>
                        showTargetPriceSuppliers
                          ? setTargetPriceModalTab('notify')
                          : closeTargetPriceModal()
                      }
                      disabled={isSavingTargetPrice}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleSaveTargetPrice}
                      disabled={isSavingTargetPrice}
                    >
                      {isSavingTargetPrice ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-floppy-disk mr-1" />
                          Save
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => closeTargetPriceModal()}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showTargetPriceModal ? (
        <div
          className={`modal-backdrop fade${targetPriceModalAnimating ? ' show' : ''}`}
        />
      ) : null}
    </div>
  )
}

export default RfqView
